import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Mail, 
  Globe,
  Send,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.25, 1, 0.5, 1] }
  }
};

export default function Contact() {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service_interest: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const serviceOptions = [
    { value: 'ehs', label: language === 'en' ? 'EHS Consultancy' : 'استشارات الصحة والسلامة والبيئة' },
    { value: 'risk', label: language === 'en' ? 'Risk Management & Compliance' : 'إدارة المخاطر والامتثال' },
    { value: 'pmo', label: language === 'en' ? 'Project Management & PMO' : 'إدارة المشاريع ومكتب إدارة المشاريع' },
    { value: 'payroll', label: language === 'en' ? 'Payroll & Tax Compliance' : 'الرواتب والامتثال الضريبي' },
    { value: 'it', label: language === 'en' ? 'IT Consulting' : 'استشارات تكنولوجيا المعلومات' },
    { value: 'general', label: language === 'en' ? 'General Inquiry' : 'استفسار عام' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service_interest: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        language
      });
      
      setStatus({ 
        type: 'success', 
        message: t.contact.form.success 
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service_interest: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setStatus({ 
        type: 'error', 
        message: t.contact.form.error 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: t.contact.info.location,
      value: t.contact.info.locationValue,
      color: 'text-secondary'
    },
    {
      icon: Mail,
      title: t.contact.info.email,
      value: t.contact.info.emailValue,
      href: 'mailto:info@safeproglobal.com',
      color: 'text-primary'
    },
    {
      icon: Globe,
      title: t.contact.info.website,
      value: t.contact.info.websiteValue,
      href: 'https://www.safeproglobal.com',
      color: 'text-secondary'
    }
  ];

  return (
    <div className="min-h-screen" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-24 bg-primary overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(
              45deg,
              transparent,
              transparent 20px,
              white 20px,
              white 21px
            )`
          }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground uppercase tracking-tight mb-6">
              {t.contact.title}
            </h1>
            <p className="text-xl text-primary-foreground/80 max-w-3xl mx-auto">
              {t.contact.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 bg-background" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="lg:col-span-2 space-y-6"
            >
              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-primary uppercase tracking-tight mb-8">
                  {language === 'en' ? 'Get in Touch' : 'تواصل معنا'}
                </h2>
              </motion.div>

              {contactInfo.map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div key={index} variants={itemVariants}>
                    <Card className="rounded-none border-border hover:border-secondary transition-colors duration-300">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 bg-muted flex items-center justify-center flex-shrink-0`}>
                            <Icon className={`w-6 h-6 ${item.color}`} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-primary uppercase tracking-wide text-sm mb-1">
                              {item.title}
                            </h3>
                            {item.href ? (
                              <a 
                                href={item.href}
                                target={item.href.startsWith('http') ? '_blank' : undefined}
                                rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                                className="text-muted-foreground hover:text-secondary transition-colors duration-200"
                              >
                                {item.value}
                              </a>
                            ) : (
                              <p className="text-muted-foreground">{item.value}</p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}

              {/* Operating Hours */}
              <motion.div variants={itemVariants}>
                <Card className="rounded-none border-secondary bg-secondary/5">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-primary uppercase tracking-wide text-sm mb-4">
                      {language === 'en' ? 'Operating Hours' : 'ساعات العمل'}
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          {language === 'en' ? 'Monday - Saturday' : 'الإثنين - السبت'}
                        </span>
                        <span className="font-medium text-foreground">9:00 AM - 6:00 PM</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          {language === 'en' ? 'Sunday' : 'الأحد'}
                        </span>
                        <span className="font-medium text-foreground">
                          {language === 'en' ? 'Closed' : 'مغلق'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-3"
            >
              <Card className="rounded-none border-2 border-border">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    {/* Status Message */}
                    {status.message && (
                      <div 
                        className={`flex items-center gap-3 p-4 ${
                          status.type === 'success' 
                            ? 'bg-secondary/10 text-secondary border border-secondary/30' 
                            : 'bg-destructive/10 text-destructive border border-destructive/30'
                        }`}
                        data-testid="form-status"
                      >
                        {status.type === 'success' ? (
                          <CheckCircle className="w-5 h-5 flex-shrink-0" />
                        ) : (
                          <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        )}
                        <span>{status.message}</span>
                      </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Name */}
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-sm font-semibold uppercase tracking-wide">
                          {t.contact.form.name} *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="rounded-none border-border focus:border-primary"
                          data-testid="input-name"
                        />
                      </div>

                      {/* Email */}
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-sm font-semibold uppercase tracking-wide">
                          {t.contact.form.email} *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="rounded-none border-border focus:border-primary"
                          data-testid="input-email"
                        />
                      </div>

                      {/* Phone */}
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-sm font-semibold uppercase tracking-wide">
                          {t.contact.form.phone}
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          className="rounded-none border-border focus:border-primary"
                          data-testid="input-phone"
                        />
                      </div>

                      {/* Company */}
                      <div className="space-y-2">
                        <Label htmlFor="company" className="text-sm font-semibold uppercase tracking-wide">
                          {t.contact.form.company}
                        </Label>
                        <Input
                          id="company"
                          name="company"
                          type="text"
                          value={formData.company}
                          onChange={handleChange}
                          className="rounded-none border-border focus:border-primary"
                          data-testid="input-company"
                        />
                      </div>
                    </div>

                    {/* Service of Interest */}
                    <div className="space-y-2">
                      <Label htmlFor="service" className="text-sm font-semibold uppercase tracking-wide">
                        {t.contact.form.service}
                      </Label>
                      <Select 
                        value={formData.service_interest} 
                        onValueChange={handleServiceChange}
                      >
                        <SelectTrigger 
                          className="rounded-none border-border"
                          data-testid="select-service"
                        >
                          <SelectValue placeholder={t.contact.form.selectService} />
                        </SelectTrigger>
                        <SelectContent>
                          {serviceOptions.map((option) => (
                            <SelectItem 
                              key={option.value} 
                              value={option.value}
                              data-testid={`service-option-${option.value}`}
                            >
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Message */}
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-sm font-semibold uppercase tracking-wide">
                        {t.contact.form.message} *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        className="rounded-none border-border focus:border-primary resize-none"
                        data-testid="input-message"
                      />
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none py-6 font-semibold uppercase tracking-wide"
                      data-testid="submit-button"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="animate-spin me-2">⏳</span>
                          {t.contact.form.sending}
                        </>
                      ) : (
                        <>
                          <Send className="me-2 w-5 h-5" />
                          {t.contact.form.submit}
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
